import React from 'react';

/**
 * Reusable Input Component.
 * Styles with Tailwind CSS, supporting various types and focus states.
 * Now includes prop for displaying validation errors.
 *
 * @param {object} props - Component props.
 * @param {string} [props.type='text'] - Input type.
 * @param {string} [props.id] - ID for the input, also used for label's htmlFor.
 * @param {string} [props.name] - Name attribute for the input.
 * @param {string} [props.label] - Optional label text for the input.
 * @param {string} [props.placeholder=''] - Placeholder text.
 * @param {string} [props.value] - Controlled component value.
 * @param {function} [props.onChange] - Change event handler.
 * @param {boolean} [props.required=false] - If true, input is required.
 * @param {boolean} [props.disabled=false] - If true, input is disabled.
 * @param {string} [props.className=''] - Additional custom Tailwind classes for the input element.
 * @param {string} [props.labelClassName=''] - Additional custom Tailwind classes for the label element.
 * @param {string} [props.containerClassName=''] - Additional custom Tailwind classes for the wrapper div.
 * @param {string} [props.error=''] - Error message to display below the input.
 */
const Input = ({
	type = 'text',
	id,
	name,
	label,
	placeholder = '',
	value,
	onChange,
	required = false,
	disabled = false,
	step = "1",
	className = '',
	labelClassName = '',
	containerClassName = '',
	readOnly = false,
	error = '',
	trailingIcon,
	...props
}) => {
	const inputBaseStyles = 'w-full px-4 py-2 border rounded-lg ' +
		'focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gmail-blue ' +
		'transition-all duration-200 ease-in-out ' +
		'placeholder-gray-400 text-text-primary disabled:bg-gray-light disabled:cursor-not-allowed';

	// Add error border style if error prop is present
	const errorStyles = error ? 'border-gmail-red' : 'border-gray-medium';

	// Add pr-10 when there's a trailing icon to make space for it
	const paddingStyles = trailingIcon ? 'pr-10' : 'pr-4';

	return (
		<div className={`mb-4 ${containerClassName}`}>
			{label && (
				<label htmlFor={id || name} className={`block text-sm font-medium text-text-secondary mb-1 ${labelClassName}`}>
					{label}
					{required && <span className="text-gmail-red ml-1">*</span>}
				</label>
			)}
			<div className="relative">
				<input
					type={type}
					step={step}
					id={id || name}
					name={name}
					placeholder={placeholder}
					value={value}
					onChange={onChange}
					readOnly={readOnly}
					required={required}
					disabled={disabled}
					className={`${inputBaseStyles} ${errorStyles} ${paddingStyles} ${className}`}
					{...props}
				/>
				{/* Render trailing icon if provided */}
				{trailingIcon && (
					<div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
						{trailingIcon}
          			</div>
				)}
				{/* Error message display */}
				{/* Moved error message display to the SignupBusinessPage for consistent spacing */}
				{/* {error && <p className="text-gmail-red text-xs mt-1">{error}</p>} */}
			</div>
		</div>
	);
};

export default Input;
